import { buildLexer, expectEOF, expectSingleResult, rule, } from "typescript-parsec";
import { alt, apply, kmid, lrec_sc, opt, rep, rep_sc, seq, tok, } from "typescript-parsec";
import { TokenKind, } from "./ast";
import { nodeRange } from "./ast-navigation";
const lexer = buildLexer([
    [true, /^\d+(\.\d+)?/g, TokenKind.Number],
    // Text literal: double quotes, no escapes, single line ("TC", "Not detected").
    [true, /^"[^"\n]*"/g, TokenKind.String],
    // Keyword logical operators are reserved — they must be lexed before the
    // identifier rule so `and`/`or`/`not` never tokenise as identifiers. The
    // `\b` boundary keeps `android`, `normal`, etc. as ordinary identifiers.
    [true, /^and\b/g, TokenKind.And],
    [true, /^or\b/g, TokenKind.Or],
    [true, /^not\b/g, TokenKind.Not],
    [true, /^[a-zA-Z_][a-zA-Z0-9_]*/g, TokenKind.Identifier],
    // Multi-character comparison operators before single-character ones.
    [true, /^==/g, TokenKind.Eq],
    [true, /^!=/g, TokenKind.Neq],
    [true, /^>=/g, TokenKind.Gte],
    [true, /^<=/g, TokenKind.Lte],
    [true, /^>/g, TokenKind.Gt],
    [true, /^</g, TokenKind.Lt],
    [true, /^\+/g, TokenKind.Add],
    [true, /^\-/g, TokenKind.Sub],
    [true, /^\*/g, TokenKind.Mul],
    [true, /^\//g, TokenKind.Div],
    [true, /^\(/g, TokenKind.LParen],
    [true, /^\)/g, TokenKind.RParen],
    [true, /^,/g, TokenKind.Comma],
    [true, /^\./g, TokenKind.Dot],
    [false, /^\s+/g, TokenKind.Space],
]);
const IDENT = tok(TokenKind.Identifier);
const LPAREN = tok(TokenKind.LParen);
const RPAREN = tok(TokenKind.RParen);
const DOT = tok(TokenKind.Dot);
const COMMA = tok(TokenKind.Comma);
const ADD = tok(TokenKind.Add);
const SUB = tok(TokenKind.Sub);
const MUL = tok(TokenKind.Mul);
const DIV = tok(TokenKind.Div);
const NOT = tok(TokenKind.Not);
const AND = tok(TokenKind.And);
const OR = tok(TokenKind.Or);
const NUMBER = tok(TokenKind.Number);
const STRING = tok(TokenKind.String);
// Comparison operator token → AST operator string.
const COMPARISON_OP = alt(tok(TokenKind.Eq), tok(TokenKind.Neq), tok(TokenKind.Gte), tok(TokenKind.Lte), tok(TokenKind.Gt), tok(TokenKind.Lt));
function comparisonOperator(token) {
    switch (token.kind) {
        case TokenKind.Eq:
            return "==";
        case TokenKind.Neq:
            return "!=";
        case TokenKind.Gte:
            return ">=";
        case TokenKind.Lte:
            return "<=";
        case TokenKind.Gt:
            return ">";
        case TokenKind.Lt:
            return "<";
        default:
            throw new Error(`Not a comparison operator: ${token.text}`);
    }
}
const tokenEnd = (token) => token.pos.index + token.text.length;
/** The span covering one or more child nodes (smallest start, largest end). */
function spanNodes(...nodes) {
    let start;
    let end;
    for (const n of nodes) {
        const r = nodeRange(n);
        if (!r)
            continue;
        if (start === undefined || r.start < start)
            start = r.start;
        if (end === undefined || r.end > end)
            end = r.end;
    }
    return start !== undefined && end !== undefined ? { start, end } : undefined;
}
/** Best-effort start offset of a parsed node, for member-access spans. */
function nodeStart(node) {
    switch (node.type) {
        case "FunctionCall":
            return node.nameRange?.start;
        case "VariableAccess":
            return node.range?.start;
        case "MemberAccess":
            return node.range?.start ?? nodeStart(node.object);
        default:
            return undefined;
    }
}
// Forward declarations
const EXPR = rule();
const ARGUMENTS = rule();
// Literal number or text
const LITERAL_NUM = rule();
LITERAL_NUM.setPattern(alt(apply(NUMBER, (token) => {
    return {
        type: "Literal",
        value: +token.text,
        range: { start: token.pos.index, end: tokenEnd(token) },
    };
}), apply(STRING, (token) => {
    return {
        type: "Literal",
        value: token.text.slice(1, -1),
        range: { start: token.pos.index, end: tokenEnd(token) },
    };
})));
// Variable access: identifier [.property]* — bare-identifier projection
// (`upstream.ph`, `row.x`, `sample.test.attr`, `measurements.temp`). Member
// access on call/group results is handled separately by POSTFIX_TERM.
const VARIABLE_ACCESS_NODE = rule();
VARIABLE_ACCESS_NODE.setPattern(apply(seq(IDENT, rep(seq(DOT, IDENT))), ([firstToken, propsSeq]) => {
    const lastToken = propsSeq.length === 0 ? firstToken : propsSeq[propsSeq.length - 1][1];
    return {
        type: "VariableAccess",
        identifier: firstToken.text,
        properties: propsSeq.map((p) => p[1].text),
        range: { start: firstToken.pos.index, end: tokenEnd(lastToken) },
    };
}));
// Function call: funcName(arg1, arg2)
const FUNCTION_CALL_NODE = rule();
FUNCTION_CALL_NODE.setPattern(apply(
// Keep the closing paren token (kmid would drop it) so the full-call span
// covers `name(…)` — needed for hover/value-inspection on the whole call.
seq(IDENT, LPAREN, ARGUMENTS, RPAREN), ([funcNameToken, , args, rparenToken]) => ({
    type: "FunctionCall",
    functionName: funcNameToken.text,
    arguments: args,
    nameRange: { start: funcNameToken.pos.index, end: tokenEnd(funcNameToken) },
    range: { start: funcNameToken.pos.index, end: tokenEnd(rparenToken) },
})));
// Arguments: expr1, expr2, ... (can be empty)
ARGUMENTS.setPattern(apply(opt(seq(EXPR, rep(seq(COMMA, EXPR)))), (value) => {
    if (!value)
        return [];
    const [first, restArgs] = value;
    return [first, ...restArgs.map((r) => r[1])];
}));
// A receiver eligible for postfix member access: a function call or a
// parenthesised expression. Bare identifiers keep their own `.prop` chain via
// VARIABLE_ACCESS_NODE, which avoids an ambiguous double-parse of `a.b`.
const CALL_OR_GROUP = rule();
CALL_OR_GROUP.setPattern(alt(FUNCTION_CALL_NODE, kmid(LPAREN, EXPR, RPAREN)));
// Postfix term: `<call|group>(.IDENT)*` folded into MemberAccess chain, or a
// literal, or a bare variable access.
const POSTFIX_TERM = rule();
POSTFIX_TERM.setPattern(alt(apply(seq(CALL_OR_GROUP, rep_sc(seq(DOT, IDENT))), ([head, props]) => {
    let node = head;
    for (const [, idToken] of props) {
        const propRange = {
            start: idToken.pos.index,
            end: tokenEnd(idToken),
        };
        node = {
            type: "MemberAccess",
            object: node,
            property: idToken.text,
            range: { start: nodeStart(node) ?? propRange.start, end: propRange.end },
            propertyRange: propRange,
        };
    }
    return node;
}), LITERAL_NUM, VARIABLE_ACCESS_NODE));
// Unary: +x, -x, -(a*b)
const UNARY_TERM = rule();
UNARY_TERM.setPattern(alt(apply(seq(alt(ADD, SUB), UNARY_TERM), ([opToken, operandNode]) => ({
    type: "UnaryOperation",
    operator: opToken.kind,
    operand: operandNode,
    range: {
        start: opToken.pos.index,
        end: nodeRange(operandNode)?.end ?? tokenEnd(opToken),
    },
})), POSTFIX_TERM));
// Multiplicative: * /
const MUL_DIV_TERM = rule();
MUL_DIV_TERM.setPattern(lrec_sc(UNARY_TERM, seq(alt(MUL, DIV), UNARY_TERM), (leftNode, [opToken, rightNode]) => ({
    type: "BinaryOperation",
    operator: opToken.kind,
    left: leftNode,
    right: rightNode,
    range: spanNodes(leftNode, rightNode),
})));
// Additive: + -
const ADDITIVE_TERM = rule();
ADDITIVE_TERM.setPattern(lrec_sc(MUL_DIV_TERM, seq(alt(ADD, SUB), MUL_DIV_TERM), (leftNode, [opToken, rightNode]) => ({
    type: "BinaryOperation",
    operator: opToken.kind,
    left: leftNode,
    right: rightNode,
    range: spanNodes(leftNode, rightNode),
})));
// Comparison: a single (non-chained) numeric comparison → boolean.
const COMPARISON_TERM = rule();
COMPARISON_TERM.setPattern(apply(seq(ADDITIVE_TERM, opt(seq(COMPARISON_OP, ADDITIVE_TERM))), ([left, rest]) => {
    if (!rest)
        return left;
    const [opToken, right] = rest;
    return {
        type: "Comparison",
        operator: comparisonOperator(opToken),
        left,
        right,
        range: spanNodes(left, right),
    };
}));
// `not <boolean>` — binds looser than comparison so `not a > b` is `not (a > b)`.
const NOT_TERM = rule();
NOT_TERM.setPattern(alt(apply(seq(NOT, NOT_TERM), ([notToken, operand]) => ({
    type: "Not",
    operand,
    range: {
        start: notToken.pos.index,
        end: nodeRange(operand)?.end ?? tokenEnd(notToken),
    },
})), COMPARISON_TERM));
// Logical AND
const AND_TERM = rule();
AND_TERM.setPattern(lrec_sc(NOT_TERM, seq(AND, NOT_TERM), (leftNode, [, rightNode]) => ({
    type: "Logical",
    operator: "and",
    left: leftNode,
    right: rightNode,
    range: spanNodes(leftNode, rightNode),
})));
// Logical OR (lowest precedence) — top-level expression entry point.
EXPR.setPattern(lrec_sc(AND_TERM, seq(OR, AND_TERM), (leftNode, [, rightNode]) => ({
    type: "Logical",
    operator: "or",
    left: leftNode,
    right: rightNode,
    range: spanNodes(leftNode, rightNode),
})));
/**
 * Parse a formula string into an AST and collect top-level variable dependencies.
 */
export function parseFormula(formula) {
    if (!formula || formula.trim().length === 0) {
        throw new Error("Formula is empty");
    }
    const tokenStream = lexer.parse(formula);
    const ast = expectSingleResult(expectEOF(EXPR.parse(tokenStream)));
    const dependencies = new Set();
    const collectDeps = (node) => {
        switch (node.type) {
            case "Literal":
                return;
            case "VariableAccess":
                dependencies.add(node.identifier);
                return;
            case "FunctionCall":
                node.arguments.forEach(collectDeps);
                return;
            case "MemberAccess":
                collectDeps(node.object);
                return;
            case "BinaryOperation":
            case "Comparison":
            case "Logical":
                collectDeps(node.left);
                collectDeps(node.right);
                return;
            case "UnaryOperation":
                collectDeps(node.operand);
                return;
            case "Not":
                collectDeps(node.operand);
                return;
        }
    };
    collectDeps(ast);
    return { ast, dependencies };
}
/**
 * Extract upstream codes referenced via `upstream.code_name` in a formula.
 * Returns the set of code names (without the `upstream.` prefix).
 */
export function extractUpstreamCodes(formula) {
    const { ast } = parseFormula(formula);
    const codes = new Set();
    const walk = (node) => {
        switch (node.type) {
            case "Literal":
                return;
            case "VariableAccess":
                if (node.identifier === "upstream" && node.properties.length > 0) {
                    codes.add(node.properties[0]);
                }
                return;
            case "FunctionCall":
                node.arguments.forEach(walk);
                return;
            case "MemberAccess":
                walk(node.object);
                return;
            case "BinaryOperation":
            case "Comparison":
            case "Logical":
                walk(node.left);
                walk(node.right);
                return;
            case "UnaryOperation":
                walk(node.operand);
                return;
            case "Not":
                walk(node.operand);
                return;
        }
    };
    walk(ast);
    return codes;
}
//# sourceMappingURL=parser.js.map